import { ContentCopyOutlined, HelpOutlined, OpenInNewOutlined } from '@mui/icons-material'
import { Box, Chip, IconButton, Stack, Tooltip } from '@mui/material'
import Paper from '@mui/material/Paper'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink } from 'react-router-dom'

import CustomTypography from '@/components/dataDisplay/CustomTypography'
import { useAlertContext } from '@/contexts/AlertContext'
import { ResourceRouteInformation } from '@/features/resource/constants'
import type { Resource } from '@/features/resource/types'
import { getResourceTypeTranslation } from '@/features/resource/utils/resourceTypes'

type ResourceJsonInfoPanelProps = {
  resources: Resource[]
  isError: boolean
}

export function ResourceJsonInfoPanel({ resources, isError }: Readonly<ResourceJsonInfoPanelProps>) {
  const { t } = useTranslation()

  return (
    <Paper sx={{ p: 3, marginBottom: 2 }}>
      <Stack spacing={2}>
        <Stack alignItems="center" direction="row" spacing={1}>
          <HelpOutlined />
          <CustomTypography sx={{ mb: 2 }} variant="h3">
            {t('controllers.configuration.resource_json.info.label')}
          </CustomTypography>
        </Stack>
        <CustomTypography sx={{ whiteSpace: 'pre-wrap' }}>
          {t('controllers.configuration.resource_json.info.text')}
        </CustomTypography>
        <Box>
          <CustomTypography color="grey.500" variant="caption">
            Resource definition
          </CustomTypography>
          <CustomTypography sx={{ whiteSpace: 'pre-wrap' }}>{`• Identity (id, type)
• Hardware binding to app.json (battery + optional power meter)`}</CustomTypography>
        </Box>
        <Box>
          <CustomTypography color="grey.500" variant="caption">
            Steering configuration
          </CustomTypography>
          <CustomTypography
            sx={{ whiteSpace: 'pre-wrap' }}
          >{`• State-of-charge thresholds (hard limits and telemetry warnings)
• Charge and discharge ramp speeds
• Transformer-guard protection
• Trickle-charge mode
• Market participation (FCR, aFRR, mFRR, FFR, wholesale)`}</CustomTypography>
        </Box>
        <ResourceList isError={isError} resources={resources} />
      </Stack>
    </Paper>
  )
}

function ResourceList({ isError, resources }: Readonly<{ isError: boolean; resources: Resource[] }>) {
  const { t } = useTranslation()

  return (
    <Box>
      <CustomTypography color="grey.500" variant="caption">
        {t('controllers.configuration.resource_json.info.resources_list.label')}
      </CustomTypography>
      <ResourceListContent isError={isError} resources={resources} />
    </Box>
  )
}

function ResourceListContent({ isError, resources }: Readonly<{ isError: boolean; resources: Resource[] }>) {
  const { t } = useTranslation()

  if (isError) {
    return <CustomTypography>{t('controllers.configuration.resource_json.info.resources_list.error')}</CustomTypography>
  }

  if (resources.length === 0) {
    return <CustomTypography>{t('controllers.configuration.resource_json.info.resources_list.empty')}</CustomTypography>
  }

  // Sort by resourceID so the panel matches Monaco's autocomplete order, which alphabetises enum values internally.
  const sortedResources = [...resources].sort((a, b) => a.resourceID.localeCompare(b.resourceID))

  return (
    <Stack spacing={1.5} sx={{ mt: 1 }}>
      {sortedResources.map((resource) => (
        <ResourceRow key={resource.resourceID} resource={resource} />
      ))}
    </Stack>
  )
}

function ResourceRow({ resource }: Readonly<{ resource: Resource }>) {
  const { t } = useTranslation()
  const { pushAlert } = useAlertContext()

  const copyResourceId = async () => {
    try {
      await navigator.clipboard.writeText(resource.resourceID)
      pushAlert({
        message: t('controllers.configuration.resource_json.info.resources_list.copy_success'),
        severity: 'success',
      })
    } catch {
      pushAlert({
        message: t('controllers.configuration.resource_json.info.resources_list.copy_failed'),
        severity: 'error',
      })
    }
  }

  return (
    <Stack direction="column" spacing={0.5}>
      <Stack alignItems="center" direction="row" spacing={1}>
        <CustomTypography sx={{ fontWeight: 500 }}>{resource.resourceName ?? resource.resourceID}</CustomTypography>
        {resource.resourceType && (
          <Chip label={getResourceTypeTranslation(resource.resourceType, t)} size="small" variant="outlined" />
        )}
        <Tooltip title={t('controllers.configuration.resource_json.info.resources_list.open_resource_tooltip')}>
          <IconButton
            component={RouterLink}
            rel="noopener noreferrer"
            size="small"
            target="_blank"
            to={ResourceRouteInformation.RESOURCE_DETAILS.navigationPath(resource.resourceID)}
          >
            <OpenInNewOutlined fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
      <Stack alignItems="center" direction="row" spacing={1}>
        <CustomTypography color="grey.700" sx={{ fontFamily: 'monospace' }} variant="caption">
          {resource.resourceID}
        </CustomTypography>
        <Tooltip title={t('controllers.configuration.resource_json.info.resources_list.copy_id_tooltip')}>
          <IconButton size="small" onClick={copyResourceId}>
            <ContentCopyOutlined fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
    </Stack>
  )
}
